import {ensureValueOf} from '../../errorCodes/utils/zod';
import {TypedEventTarget} from '../../logics/TypedEventTarget';
import {CRP_VERSION, ETextParamType_SetCRPMode} from '../../logics/const';
import {ensureScriptLoaded} from '../../logics/externalScriptLoader';
import {hel_player_SetPriority} from '../../logics/misc';
import {hel_setText, requestSendingReceivedPacketsFromSkyway} from '../../logics/moduleCaller';
import {GetIHeliNetwork} from '../../singleton_registry';
import {rtcConfigSchema} from '../../types/network';
import {
  onNetConnectionError,
  onNetCreatedPlayerID,
  onNetPosConnected,
  onNetPosDisconnected
} from '../networkCallback';

import type {
  IHeliPositionChannelProvider,
  PositionChannelProviderEventMap
} from './IHeliPositionChannelProvider';
import type {CRPFront} from '../../../../CRPClient/dist/scripts/classes/crpFront';
import type {createFront} from '../../../../CRPClient/dist/scripts/index';
import type {
  onRemoteJoinHandler,
  onRemoteLeaveHandler
} from '../../../../CRPClient/dist/scripts/types/frontHandlers';
import type {CRPConnectionType} from '../../../../CRPClient/dist/scripts/types/misc';
import type {CHeliPlayer} from '../CHeliPlayer';
import type {CHeliPositionChannel} from '../CHeliPositionChannel';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var createCrpFront: typeof createFront | undefined;
  /* eslint-enable */
}

/**
 * ブラウザコンソールからのデバッグのために、グローバル変数へ保存する。
 * @see https://discord.com/channels/596169759045976094/1150648243387125790/1329045516822122588
 */
const registerCrpFrontAsGlobalVariableForDebug = (crpFront: CRPFront) => {
  Object.assign(globalThis, {crpFront});
};

class CRPFrontEventMultiplexer {
  #remoteJoinListeners = new Set<onRemoteJoinHandler>();
  #remoteLeaveListeners = new Set<onRemoteLeaveHandler>();

  constructor(crpFront: CRPFront) {
    crpFront.onRemoteJoin((params) => {
      for (const listener of this.#remoteJoinListeners) {
        try {
          listener(params);
        } catch (e) {
          console.error('onRemoteJoin error', e);
        }
      }
    });

    crpFront.onRemoteLeave((params) => {
      for (const listener of this.#remoteLeaveListeners) {
        try {
          listener(params);
        } catch (e) {
          console.error('onRemoteLeave error', e);
        }
      }
    });
  }

  addRemoteJoinListener(fn: onRemoteJoinHandler) {
    this.#remoteJoinListeners.add(fn);
  }

  addRemoteLeaveListener(fn: onRemoteLeaveHandler) {
    this.#remoteLeaveListeners.add(fn);
  }
}

export class CHeliCarnelianPositionChannelProvider
  extends TypedEventTarget<PositionChannelProviderEventMap>
  implements IHeliPositionChannelProvider
{
  #crpFront: {
    instance: CRPFront;
    eventTarget: CRPFrontEventMultiplexer;
  } | null = null;
  #presenterIdList: string[] = [];
  #reOpenCount = 0;

  protected readonly connectionType: CRPConnectionType = 'normal';

  async ensureLibraryLoadedAndInitialized(): Promise<void> {
    await ensureScriptLoaded('https://heliodor.vketcdn.com/crp/common/v2.0/crp-latest.js');
  }

  isConnectedToPositionChannel(positionChannel: CHeliPositionChannel): boolean {
    if (!this.#crpFront) {
      throw new Error('#crpFront is null.');
    }
    return this.#crpFront.instance.ownUser?.positionChannelCode ===
      positionChannel.getChannelId() && this.#crpFront.instance.ownObjectId
      ? true
      : false;
  }

  async connect(): Promise<boolean> {
    return true;
  }

  isConnected(): boolean {
    if (!this.#crpFront) {
      throw new Error('#crpFront is null.');
    }
    return this.#crpFront.instance.isJoined;
  }

  isConnectedPositionChannel(): boolean {
    if (!this.#crpFront) {
      throw new Error('#crpFront is null.');
    }
    return this.#crpFront.instance.isEntered;
  }

  async joinPositionChannel(channelSessionJwt: string): Promise<void> {
    hel_setText(ETextParamType_SetCRPMode, '1');
    this.#crpFrontInitialize();
    if (!this.#crpFront) {
      throw new Error('#crpFront is null.');
    }
    console.debug('CRP open start');
    this.#crpFront.instance.sendMovement([0, 0, 0], [0, 0, 0, 0], [0, 0, 0], true, 0);
    await this.#crpFront.instance.open(channelSessionJwt);
    console.debug('CRP open done');
  }

  async leavePositionChannel(): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    if (!this.#crpFront) {
      // NOTE: 本メソッドは入室失敗時後でも処理に呼ばれるが、一度も入室に成功していない場合、this.#crpFront は null になる
      return;
    }

    heliNetwork.releaseAllPlayer();
    await this.#crpFront.instance.close();
  }

  getPlayerId(): string | null {
    if (!this.#crpFront) {
      throw new Error('#crpFront is null.');
    }
    return this.#crpFront.instance.ownObjectId ?? null;
  }

  sendData(param: string): void {
    if (!this.#crpFront) {
      // throw new Error('#crpFront is null.');
      // nullで来る場合もある（RTC接続中の時）nullの場合は何もしない
      return;
    }
    this.#crpFront.instance.sendData(param);
  }

  // 他プレーヤのアバター表示切替コールバック
  setPlayerFullAvatarState(player: CHeliPlayer, isFullAvatar: boolean): void {
    const prevHighFreqSync = this.#isHighFrequentSyncTarget(player);
    player.fullAvatarState = isFullAvatar;
    this.#checkDispatchHighFrequentChange(prevHighFreqSync, player);
  }
  // 他プレーヤの他プレーヤのモーショントラッキング使用状況切替コールバック
  setPlayerMotionTrackingState(player: CHeliPlayer, isMotionTracking: boolean): void {
    const prevHighFreqSync = this.#isHighFrequentSyncTarget(player);
    player.motionTrackingState = isMotionTracking;
    this.#checkDispatchHighFrequentChange(prevHighFreqSync, player);
  }
  // TODO
  // コールバック実行毎に下記関数を呼び出すのではなく、HeliPlayer側にEvent発火機能を実装したい
  #checkDispatchHighFrequentChange(prevState: boolean, player: CHeliPlayer) {
    if (!prevState && this.#isHighFrequentSyncTarget(player)) {
      this.sendHighFrequentSyncSubscribe(player, true);
    }
    if (prevState && !this.#isHighFrequentSyncTarget(player)) {
      this.sendHighFrequentSyncSubscribe(player, false);
    }
  }
  /** 高頻度同期対象か判定 */
  #isHighFrequentSyncTarget(player: CHeliPlayer): boolean {
    if (player.getRole() === 'presenter') {
      return true;
    }
    if (player.fullAvatarState && player.motionTrackingState) {
      return true;
    }
    return false;
  }
  sendHighFrequentSyncSubscribe(player: CHeliPlayer, subscribe: boolean): void {
    if (subscribe) {
      this.sendData(`subscribe:${player.getPlayerId()}`);
    } else {
      this.sendData(`unsubscribe:${player.getPlayerId()}`);
    }
  }

  /** Crp初期化 */
  #crpFrontInitialize() {
    const config = getConfig();
    const crpEndPoint = `wss://${config.crpDomain}/ws/${CRP_VERSION}/`;
    const prismEndpoint = `https://${config.crpPrismDomain}/api/${CRP_VERSION}/endpoint`;
    if (!globalThis.createCrpFront) {
      throw new Error('createCrpFront is not defined');
    }

    let crpFrontOption = {
      connectionType: this.connectionType,
      prismEndpoint: prismEndpoint
    };

    const iHeliNetwork = GetIHeliNetwork();
    if (!iHeliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    const positionChannel = iHeliNetwork.getCurrentPositionChannel();
    if (!positionChannel) {
      throw new Error('currentPositionChannel returns null.');
    }

    const rtcConfig = positionChannel.getValue('rtc_config');
    // もし設定があれば利用する
    if (rtcConfig) {
      ensureValueOf(rtcConfigSchema, rtcConfig);
      const carnelianConfig = rtcConfig.services?.carnelian;
      if (carnelianConfig) {
        crpFrontOption = {
          ...crpFrontOption,
          ...carnelianConfig
        };
      }
    }

    const crpFrontBase = globalThis.createCrpFront(crpEndPoint, crpFrontOption);
    this.#crpFront = {
      instance: crpFrontBase,
      eventTarget: new CRPFrontEventMultiplexer(crpFrontBase)
    };
    this.#crpFront.eventTarget.addRemoteJoinListener((context) => {
      this.dispatchEvent<'remotejoin'>(
        new CustomEvent('remotejoin', {
          detail: {playerId: context.objectId}
        })
      );
    });
    this.#crpFront.eventTarget.addRemoteLeaveListener((context) => {
      this.dispatchEvent<'remoteleave'>(
        new CustomEvent('remoteleave', {
          detail: {
            playerId: context.objectId
          }
        })
      );
    });
    window.addEventListener('beforeunload', () => {
      crpFrontBase.close();
    });

    registerCrpFrontAsGlobalVariableForDebug(crpFrontBase);

    const crpFront = this.#crpFront;
    if (!crpFront) {
      throw new Error('#crpFront is null.');
    }

    crpFront.instance.onOpened(() => {
      console.debug('onOpened');
      crpFront.instance.sendMovement([0, 0, 0], [0, 0, 0, 0], [0, 0, 0], true, 0);
    });

    crpFront.instance.onEntered(({objectId}: {objectId: string}) => {
      console.debug('crpFront.onEntered objectId', objectId);
      requestSendingReceivedPacketsFromSkyway('open', objectId, '');
      iHeliNetwork?.sendData('start');

      // PlayerIDが使用可能になったことを通知する
      onNetCreatedPlayerID(objectId);

      onNetPosConnected();
      console.debug('CRPFRONT: 座標チャンネル接続完了');
      this.dispatchEvent<'afterjoinpositionrtc'>(new Event('afterjoinpositionrtc'));
    });

    crpFront.eventTarget.addRemoteLeaveListener((context) => {
      const {objectId, user} = context;
      if (user?.userRole === 'presenter' && this.#presenterIdList.includes(objectId)) {
        this.#presenterIdList.splice(this.#presenterIdList.indexOf(objectId), 1);
        if (this.#presenterIdList.length === 0) {
          // leave
        }
      }
    });

    // 他プレーヤー入室時
    crpFront.eventTarget.addRemoteJoinListener((info) => {
      console.debug('onRemoteJoin', info);
      const objectId = info.objectId;
      const role = info.role;
      const name = info.name;
      const avatarId = String(info.avatarId);
      const isStart = info.isStart;
      const user = info.user;
      if (!iHeliNetwork) {
        throw new Error('Initialization failed: GetIHeliNetwork() returns null.');
      }
      iHeliNetwork.createPlayer(
        objectId,
        user.userCode,
        user.channelSessionCode,
        role,
        user.rtcUserCode
      );
      const startFlag = isStart ? '1' : '0';
      const sanitizedAvatarId = this.sanitizeAvatarId(role, avatarId);

      requestSendingReceivedPacketsFromSkyway(
        'data',
        objectId,
        `join:${name}:${sanitizedAvatarId}:${startFlag}`
      );
      if (role === 'presenter') {
        console.debug('onRemoteJoin presenter');
        iHeliNetwork?.sendHighFrequentSyncSubscribe(objectId, true);
        window.setTimeout(() => {
          hel_player_SetPriority(objectId, -1);
        }, 1000);
      }
    });

    // onReceiveDataが data:xxxで送られるデータのみの送受信。
    // 基本的にonRemoteDataを使う
    crpFront.instance.onReceiveData(({objectId, data}) => {
      // 存在しないobjectIdに関してのデータ送信を行わない
      if (!crpFront.instance.objects.has(objectId)) {
        // console.error("onReceiveData objectId is not found", objectId)
        return;
      }

      this.dispatchEvent<'data'>(
        new CustomEvent('data', {
          detail: {playerId: objectId, data}
        })
      );
    });

    // customstate:はonRemoteDataとonReceiveDataに送られる
    crpFront.instance.onRemoteCustomState((objectId, value, user) => {
      console.debug('change custome state', objectId, user, value);
    });

    // onRemoteDataがonReceiveDataに加えてonJoin, onLeave等の送受信もまとめて受け取れるようにしたもの
    // 基本的にonRemoteDataだけを用いれば良い形になっている
    crpFront.instance.onRemoteData(({action, objectId, data, user}) => {
      // 存在しないobjectIdに関してのデータ送信を行わない
      if (!crpFront.instance.objects.has(objectId)) {
        // console.error("onRemoteData objectId is not found", objectId)
        return;
      }
      requestSendingReceivedPacketsFromSkyway(action, objectId, data);
    });

    crpFront.instance.onClosed((ev) => {
      console.debug(ev);

      // 座標チャンネルからの退出を通知
      onNetPosDisconnected();
    });

    crpFront.instance.onError(({requestId, code, message}) => {
      console.error(`request id: ${requestId}, code: ${code}, message: [${message}]`);
      let time = 3000;
      switch (code) {
        case 'RTC03010201':
        case 'RTC03010104':
          switch (this.#reOpenCount) {
            case 1:
              time = 5000;
              break;
            case 2:
              time = 10000;
              break;
            case 3:
              time = 30000;
              break;
            case 4:
              onNetConnectionError(code);
              return;
            default:
              time = 3000;
          }

          // 接続エラー
          // n秒後に再接続
          window.setTimeout(async () => {
            const channelSessionJwt = (
              await globalThis.heliport.v3.api.channelSessionApi.getChannelSessionJwt()
            ).data;
            if (channelSessionJwt !== null) {
              await crpFront.instance.close();
              await crpFront.instance.open(channelSessionJwt);
            }
            this.#reOpenCount++;
          }, time);
          break;
        default:
          return;
      }
    });
  }

  sanitizeAvatarId = (role: string, avatarId: string) => {
    const isProtectedAvatar = true; // protectedAvatarIdList.includes(parseInt(avatarId))
    const invalidAvatarId = isProtectedAvatar && role !== 'presenter' && role !== 'staff';
    return invalidAvatarId ? '0' : avatarId;
  };
}
